import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { supabase } from '../../services/supabaseClient'
import type { Profile } from '../../store/authSlice'
import NavBack from '../base/NavBack'
import TeamManagement from './TeamManagement'
import NotificationManager from './NotificationManager'
import CompletionMatrix from './CompletionMatrix'
import GlobalCompletionStats from './GlobalCompletionStats'
import RegistrationCodes from './RegistrationCodes'
import styles from './AdminPanel.module.css'

interface Notification {
  id: number
  tool_id: string
  message: string
  active: boolean
}

export default function AdminPanel() {
  const navigate = useNavigate()
  const profile = useAppSelector((s) => s.auth.profile)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)

  const loadData = useCallback(async () => {
    setLoading(true)

    const { data: allProfiles } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: true })

    const { data: allNotifs } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })

    setProfiles((allProfiles as Profile[]) ?? [])
    setNotifications((allNotifs as Notification[]) ?? [])
    setLoading(false)
  }, [])

  useEffect(() => {
    if (profile?.app_role === 'admin') {
      loadData()
    }
  }, [profile, loadData])

  if (profile?.app_role !== 'admin') {
    return (
      <div className={styles.page} data-testid="admin-access-denied">
        <p>Access denied. Admin role required.</p>
        <NavBack label="Dashboard" onClick={() => navigate('/')} />
      </div>
    )
  }

  if (loading) {
    return (
      <div className={styles.page} data-testid="admin-loading">
        <NavBack label="Dashboard" onClick={() => navigate('/')} />
        <h1 className={styles.title}>Admin Panel</h1>
        <div className={styles.loadingText}>Loading admin data...</div>
      </div>
    )
  }

  return (
    <div className={styles.page} data-testid="admin-panel">
      <NavBack label="Dashboard" onClick={() => navigate('/')} />
      <div className={styles.titleRow}>
        <h1 className={styles.title}>Admin Panel</h1>
        <button
          className={styles.manageBtn}
          onClick={() => navigate('/manage')}
          data-testid="admin-manage-assignments-btn"
        >
          Manage assignments &rsaquo;
        </button>
      </div>

      <TeamManagement profiles={profiles} onRefresh={loadData} />
      <RegistrationCodes />
      <NotificationManager notifications={notifications} onRefresh={loadData} />
      <GlobalCompletionStats />
      <CompletionMatrix profiles={profiles} />
    </div>
  )
}
